/**
 * @file src/local/page/LocalDuplicatesNotice.tsx
 * @description The files this device is holding twice, and the one place anybody agrees to fix it.
 *
 * A library built before a file could belong to several collections copied the bytes once per
 * collection. The store's upgrade cannot undo that, because undoing it means deleting one of two
 * entries and the copy behind it — and that is a decision, not a migration (see `localDuplicates.js`).
 *
 * So this is where the decision is made, by the person whose disk it is:
 *
 *   - it says what was found and what it would give back, in a line that can be ignored;
 *   - it lists every file behind that number, with the collections at stake, before anything runs;
 *   - it names the one thing a merge can LOSE — a second orbiter or world chosen for the same file
 *     — on the row it would be lost from, rather than in a footnote;
 *   - and each row can be left out, because "all of them" is a default and not a condition.
 *
 * Nothing here decides which row survives or what it keeps; the library does, the same way twice,
 * so what this list shows is what a merge does.
 */
import { useMemo, useState } from 'react';
import { Layers } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ScrollArea,
} from 'plantasia.space-design/react';
import { mergeLocalDuplicates } from '../localLibrary.js';
import { getT } from '../../i18n/index.js';

/** As `findLocalDuplicates` returns them; kept structural so the library owns the shape. */
export interface LocalDuplicateGroup {
  key: string;
  name: string;
  size: number;
  keep: { id: string };
  duplicates: Array<{ id: string }>;
  scopes: string[];
  reclaimBytes: number;
  adoptsAnalysis: boolean;
  conflicts: string[];
}

export interface LocalDuplicatesNoticeProps {
  groups: LocalDuplicateGroup[];
  /** What to call a collection — the page's own rule, so the two never disagree on a name. */
  scopeLabel: (key: string | null) => string;
  formatBytes: (bytes: number) => string;
  /** Re-measure and re-read once the disk has changed. */
  onMerged: () => void | Promise<void>;
}

export function LocalDuplicatesNotice({ groups, scopeLabel, formatBytes, onMerged }: LocalDuplicatesNoticeProps) {
  const t = getT();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [skipped, setSkipped] = useState(0);
  // What was ticked OFF, not what was ticked on. The list re-reads on every import, copy and
  // removal, so holding the positive set would mean deciding what to do with a group that appeared
  // after it was built; holding the refusals means everything is offered until somebody says no,
  // and a group that goes away takes nothing with it.
  const [declined, setDeclined] = useState<string[]>([]);

  const reclaim = useMemo(
    () => groups.reduce((sum, group) => sum + group.reclaimBytes, 0),
    [groups],
  );
  const chosenGroups = useMemo(() => groups.filter((group) => !declined.includes(group.key)), [groups, declined]);
  const chosenReclaim = useMemo(
    () => chosenGroups.reduce((sum, group) => sum + group.reclaimBytes, 0),
    [chosenGroups],
  );

  if (!groups.length) return null;

  const run = async () => {
    setBusy(true);
    try {
      const result = await mergeLocalDuplicates(chosenGroups);
      // A group whose rows moved while this was open is refused rather than merged into something
      // nobody agreed to. Saying so is the only way the person knows to look again.
      setSkipped(result.skipped);
      if (!result.skipped) setOpen(false);
      await onMerged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Alert>
        <Layers aria-hidden="true" />
        <AlertTitle>{t('localFiles.duplicates.title', 'Some files are here more than once')}</AlertTitle>
        <AlertDescription>
          <span>
            {reclaim > 0
              ? (t('localFiles.duplicates.lead', {
                  count: groups.length,
                  size: formatBytes(reclaim),
                  defaultValue:
                    '{{count}} files were copied once for every collection they were added to. Combining them keeps one copy of each and gives back {{size}}.',
                }) as string)
              : (t('localFiles.duplicates.leadNoBytes', {
                  count: groups.length,
                  defaultValue:
                    '{{count}} files are listed once for every collection they were added to. Combining them makes each one row again.',
                }) as string)}
          </span>
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            {t('localFiles.duplicates.review', 'Review them')}
          </Button>
        </AlertDescription>
      </Alert>

      <Dialog open={open} onOpenChange={(next) => !busy && setOpen(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('localFiles.duplicates.dialogTitle', 'Files held more than once')}</DialogTitle>
            <DialogDescription>
              {t(
                'localFiles.duplicates.dialogBody',
                'Each of these is one file on your disk that this browser copied once per collection. Combining keeps one entry that belongs to every collection it was in, with its waveform and its place on each rail. Your own files are untouched.',
              )}
            </DialogDescription>
          </DialogHeader>

          {/* The dialog does not cap its own height, and a library can hold a lot of these. */}
          <ScrollArea style={{ maxHeight: '46vh' }}>
            <ul className="ps-local-duplicates">
              {groups.map((group) => {
                const ticked = !declined.includes(group.key);
                return (
                  <li key={group.key} className="ps-local-duplicate">
                    <Checkbox
                      checked={ticked}
                      disabled={busy}
                      aria-label={group.name}
                      onCheckedChange={(next) =>
                        setDeclined((current) =>
                          next ? current.filter((key) => key !== group.key) : [...current, group.key],
                        )
                      }
                    />
                    <div className="ps-local-duplicate-text">
                      <span className="ps-local-duplicate-name" title={group.name}>
                        {group.name}
                      </span>
                      <span className="ps-local-note ps-local-muted">
                        {t('localFiles.duplicates.row', {
                          copies: group.duplicates.length + 1,
                          size: formatBytes(group.reclaimBytes),
                          defaultValue: '{{copies}} copies · {{size}} comes back',
                        }) as string}
                        {group.scopes.length
                          ? ` · ${group.scopes.map((key) => scopeLabel(key)).join(', ')}`
                          : ` · ${t('localFiles.noCollection', 'No collection')}`}
                      </span>
                      {/* The one thing a merge cannot carry: two rows, two different choices. */}
                      {group.conflicts.length ? (
                        <span className="ps-local-note ps-local-warning">
                          {group.conflicts.includes('orbiter') && group.conflicts.includes('world')
                            ? t(
                                'localFiles.duplicates.conflictBoth',
                                'These copies were given different orbiters and different worlds. The one that stays keeps its own; the other choices are dropped.',
                              )
                            : group.conflicts.includes('orbiter')
                              ? t(
                                  'localFiles.duplicates.conflictOrbiter',
                                  'These copies were given different orbiters. The one that stays keeps its own; the other choice is dropped.',
                                )
                              : t(
                                  'localFiles.duplicates.conflictWorld',
                                  'These copies were given different worlds. The one that stays keeps its own; the other choice is dropped.',
                                )}
                        </span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>

          {skipped ? (
            <p className="ps-local-note ps-local-warning">
              {t('localFiles.duplicates.skipped', {
                count: skipped,
                defaultValue:
                  '{{count}} of these changed while this was open — in another tab, most likely — and were left alone. The list has been read again.',
              })}
            </p>
          ) : null}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              {t('localFiles.cancel', 'Cancel')}
            </Button>
            <Button onClick={() => void run()} disabled={busy || !chosenGroups.length}>
              {chosenReclaim > 0
                ? (t('localFiles.duplicates.confirm', {
                    count: chosenGroups.length,
                    size: formatBytes(chosenReclaim),
                    defaultValue: 'Combine {{count}} files and free {{size}}',
                  }) as string)
                : (t('localFiles.duplicates.confirmNoBytes', {
                    count: chosenGroups.length,
                    defaultValue: 'Combine {{count}} files',
                  }) as string)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
